import { Mail } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useInviteColleague } from "@/lib/api";
import { logger } from "@/lib/logger";

const ALLOWED_DOMAIN = "boardofinnovation.com";

interface InviteMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-fill the email field when the dialog opens (e.g. from a failed search query). */
  initialEmail?: string;
}

export function InviteMemberDialog({
  open,
  onOpenChange,
  initialEmail = "",
}: InviteMemberDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Invite a Colleague</DialogTitle>
          <DialogDescription>
            Send an invitation email. Only @{ALLOWED_DOMAIN} addresses are accepted.
          </DialogDescription>
        </DialogHeader>
        {open && (
          // Remount with a fresh initial email each time the dialog opens or the
          // pre-fill changes — avoids a setState-in-effect for prop sync.
          <InviteMemberForm
            key={`${open}-${initialEmail}`}
            initialEmail={initialEmail}
            onClose={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface InviteMemberFormProps {
  initialEmail: string;
  onClose: () => void;
}

function InviteMemberForm({ initialEmail, onClose }: InviteMemberFormProps) {
  const inviteColleague = useInviteColleague();
  const [email, setEmail] = useState(initialEmail);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const sending = inviteColleague.isPending;

  const handleInvite = async () => {
    if (sending) {
      return;
    }
    setError("");
    setSuccessMsg("");

    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError("Please enter an email address.");
      return;
    }

    const domain = trimmed.split("@")[1] ?? "";
    if (domain !== ALLOWED_DOMAIN) {
      setError(`Only @${ALLOWED_DOMAIN} email addresses can be invited.`);
      return;
    }

    try {
      const result = await inviteColleague.mutateAsync({ email: trimmed });
      setSuccessMsg(
        (result?.message ?? "Invite sent.") + " They'll appear in search once they sign up.",
      );
      setEmail("");
    } catch (err) {
      logger.error("[invite-member] failed:", err);
      const body = (err as { body?: { detail?: string } })?.body;
      setError(body?.detail ?? "Failed to send invite. Please try again.");
    }
  };

  return (
    <DialogBody>
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="invite-member-email">Email address</Label>
          <div className="relative">
            <Mail className="text-muted pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
            <Input
              id="invite-member-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={`name@${ALLOWED_DOMAIN}`}
              className="pl-9"
              disabled={sending}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !sending) {
                  e.preventDefault();
                  void handleInvite();
                }
              }}
            />
          </div>
        </div>

        {error && <p className="text-destructive text-sm">{error}</p>}
        {successMsg && (
          <p className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-700 dark:bg-green-950 dark:text-green-300">
            {successMsg}
          </p>
        )}
      </div>

      <DialogFooter className="mt-4">
        <Button variant="outline" onClick={onClose} disabled={sending}>
          Cancel
        </Button>
        <Button onClick={handleInvite} disabled={sending}>
          {sending ? "Sending..." : "Send Invite"}
        </Button>
      </DialogFooter>
    </DialogBody>
  );
}
